import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  useInventoryStore, 
  useInventoryHydrated,
  InventoryCategory,
  InventoryItem,
  CATEGORY_LABELS,
} from '@/stores/inventory-store';
import { useFamilyStore } from '@/stores/family-store';
import { useAuthStore, useAuthHydrated } from '@/stores/auth-store';
import PremiumRequiredModal from '@/components/PremiumRequiredModal';
import ModalBackdrop from '@/components/ModalBackdrop';
import { useSettingsStore } from '@/stores/settings-store';
import { useShoppingListStore } from '@/stores/shopping-list-store';
import { useWaterStore } from '@/stores/water-store';
import { 
  Package, 
  Plus, 
  Search,
  AlertTriangle,
  Calendar,
  MapPin,
  Trash2,
  Pencil,
  X,
  CheckCircle2,
  Utensils,
  Heart,
  Sparkles,
  Wrench,
  Flame,
  Flashlight,
  ArrowDownAZ,
  ArrowUpAZ,
  ArrowDown01,
  ArrowUp01,
  CalendarDays,
  CalendarCheck,
  Minus,
  Copy,
  ExternalLink,
  Droplets,
  ChevronRight,
  Grid2x2,
  List,
  Layers,
  ChefHat,
  type LucideIcon,
} from 'lucide-react';
import { Target } from 'lucide-react';
import { ShoppingCart } from 'lucide-react';
import type { ComponentType, SVGProps } from 'react';

const INVENTORY_REVIEW_INTERVAL_DAYS = 180;

// Vanliga produkter per kategori för förslag
const COMMON_ITEMS: Record<InventoryCategory, { name: string; unit: string; kcalPerUnit?: number }[]> = {
  mat: [
    { name: 'Konserverade bönor', unit: 'burk', kcalPerUnit: 350 },
    { name: 'Pasta', unit: 'förp', kcalPerUnit: 1500 },
    { name: 'Ris', unit: 'kg', kcalPerUnit: 3500 },
    { name: 'Havregryn', unit: 'förp', kcalPerUnit: 1400 },
    { name: 'Konserverad tomat', unit: 'burk', kcalPerUnit: 80 },
    { name: 'Tonfisk', unit: 'burk', kcalPerUnit: 200 },
    { name: 'Knäckebröd', unit: 'förp', kcalPerUnit: 800 },
    { name: 'Nötter', unit: 'påse', kcalPerUnit: 600 },
    { name: 'Honung', unit: 'burk', kcalPerUnit: 1000 },
    { name: 'Torrjäst', unit: 'förp', kcalPerUnit: 50 },
    { name: 'Mjöl', unit: 'kg', kcalPerUnit: 3400 },
    { name: 'Socker', unit: 'kg', kcalPerUnit: 4000 },
    { name: 'Salt', unit: 'förp' },
    { name: 'Olivolja', unit: 'l', kcalPerUnit: 8000 },
    { name: 'Frystorkad mat', unit: 'st', kcalPerUnit: 500 },
    { name: 'Energibars', unit: 'st', kcalPerUnit: 250 },
    { name: 'Kaffe', unit: 'förp' },
    { name: 'Te', unit: 'förp' },
    { name: 'Choklad', unit: 'st', kcalPerUnit: 550 },
    { name: 'Kex', unit: 'förp', kcalPerUnit: 500 },
  ],
  medicin: [
    { name: 'Smärtstillande (Alvedon)', unit: 'förp' },
    { name: 'Ibuprofen', unit: 'förp' },
    { name: 'Bandage', unit: 'st' },
    { name: 'Plåster', unit: 'förp' },
    { name: 'Desinfektionsmedel', unit: 'st' },
    { name: 'Termometer', unit: 'st' },
    { name: 'Antiseptisk salva', unit: 'st' },
    { name: 'Koksaltlösning', unit: 'st' },
    { name: 'Allergimedicin', unit: 'förp' },
    { name: 'Vätskeersättning', unit: 'förp' },
    { name: 'Receptbelagda läkemedel', unit: 'förp' },
    { name: 'Första hjälpen-kit', unit: 'st' },
  ],
  hygien: [
    { name: 'Toalettpapper', unit: 'st' },
    { name: 'Handsprit', unit: 'st' },
    { name: 'Tvål', unit: 'st' },
    { name: 'Våtservetter', unit: 'förp' },
    { name: 'Schampo', unit: 'st' },
    { name: 'Tandkräm', unit: 'st' },
    { name: 'Tandborste', unit: 'st' },
    { name: 'Mensskydd', unit: 'förp' },
    { name: 'Blöjor', unit: 'förp' },
    { name: 'Diskmedel', unit: 'st' },
    { name: 'Soppåsar', unit: 'förp' },
  ],
  verktyg: [
    { name: 'Kniv', unit: 'st' },
    { name: 'Multiverktyg', unit: 'st' },
    { name: 'Hammare', unit: 'st' },
    { name: 'Skruvmejslar', unit: 'st' },
    { name: 'Tejp', unit: 'st' },
    { name: 'Rep', unit: 'st' },
    { name: 'Presenning', unit: 'st' },
    { name: 'Plastpåsar', unit: 'förp' },
    { name: 'Konservöppnare', unit: 'st' },
    { name: 'Spade', unit: 'st' },
    { name: 'Vattendunk', unit: 'st' },
    { name: 'Radio (vev/batteri)', unit: 'st' },
  ],
  varme: [
    { name: 'Sovsäck', unit: 'st' },
    { name: 'Filt', unit: 'st' },
    { name: 'Värmeljus', unit: 'st' },
    { name: 'Stormkök', unit: 'st' },
    { name: 'Bränsle (stormkök)', unit: 'st' },
    { name: 'Handvärmare', unit: 'st' },
    { name: 'Ylleunderställ', unit: 'st' },
    { name: 'Mössa', unit: 'st' },
    { name: 'Vantar', unit: 'st' },
  ],
  ljus: [
    { name: 'Ficklampa', unit: 'st' },
    { name: 'Pannlampa', unit: 'st' },
    { name: 'Batterier AA', unit: 'förp' },
    { name: 'Batterier AAA', unit: 'förp' },
    { name: 'Stearinljus', unit: 'st' },
    { name: 'Tändstickor', unit: 'förp' },
    { name: 'Tändare', unit: 'st' },
    { name: 'Fotogenlampa', unit: 'st' },
    { name: 'Solcellsladdare', unit: 'st' },
    { name: 'Powerbank', unit: 'st' },
  ],
  ovrigt: [
    { name: 'Kontanter', unit: 'st' },
    { name: 'Viktiga dokument (kopior)', unit: 'st' },
    { name: 'Extra glasögon', unit: 'st' },
    { name: 'Husdjursmat', unit: 'kg' },
    { name: 'Barnartiklar', unit: 'st' },
    { name: 'Spelkort', unit: 'st' },
    { name: 'Böcker', unit: 'st' },
  ],
  // Tomma för de ej synliga kategorierna
  kommunikation: [],
  dokument: [],
};

// Kategori-ikoner och färger (exkluderar kommunikation och dokument)
const CATEGORY_CONFIG: Partial<Record<InventoryCategory, { icon: LucideIcon; color: string }>> = {
  mat: { icon: Utensils, color: '#ffcc6b' },
  medicin: { icon: Heart, color: '#6bffb3' },
  hygien: { icon: Sparkles, color: '#6bffea' },
  verktyg: { icon: Wrench, color: '#6b8aff' },
  varme: { icon: Flame, color: '#ff6b6b' },
  ljus: { icon: Flashlight, color: '#ffa06b' },
  ovrigt: { icon: Package, color: '#b86bff' },
};

// Kategorier som visas i Förråd (exkluderar kommunikation och dokument)
const VISIBLE_CATEGORIES: InventoryCategory[] = ['mat', 'medicin', 'hygien', 'verktyg', 'varme', 'ljus', 'ovrigt'];

// Mappning från inventariekategori till inköpslistakategori
const INVENTORY_TO_SHOPPING_CATEGORY: Record<InventoryCategory, string> = {
  mat: 'Mat & Dryck',
  medicin: 'Medicin & Sjukvård',
  hygien: 'Hygien',
  verktyg: 'Utrustning',
  varme: 'Värme & Bränsle',
  ljus: 'Energi & Belysning',
  kommunikation: 'Utrustning',
  dokument: 'Övrigt',
  ovrigt: 'Övrigt',
};

// Vanliga avdelningar i en supermarket
const COMMON_AISLES = [
  'Frukt & Grönt',
  'Mejeri',
  'Kött & Chark',
  'Fisk & Skaldjur',
  'Bröd & Bageri',
  'Konserver',
  'Torrvaror',
  'Fryst',
  'Drycker',
  'Godis & Snacks',
  'Kemi & Städning',
  'Hygien',
  'Djurmat',
  'Hushåll',
  'Baby',
];

export default function InventoryPage() {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuthStore();
  const family = useFamilyStore((s) => s.family);
  const authHydrated = useAuthHydrated();
  const inventoryHydrated = useInventoryHydrated();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const {
    items,
    stats,
    addItem,
    updateItem,
    deleteItem,
    fetchFromServer,
  } = useInventoryStore();

  const waterBadgeCount = useWaterStore((s) => s.stats.containersNeedingRefresh.length);
  
  // UI State
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);
  const [isCopying, setIsCopying] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<InventoryCategory | 'all'>('all');
  const [sortBy, setSortBy] = useState<'name' | 'expiration' | 'quantity' | 'inventoryDate'>('expiration');
  const [sortAsc, setSortAsc] = useState(true);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionSearch, setSuggestionSearch] = useState('');
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [locationSearch, setLocationSearch] = useState('');
  const [confirmInventoryItem, setConfirmInventoryItem] = useState<InventoryItem | null>(null);
  const [confirmDeleteItemId, setConfirmDeleteItemId] = useState<string | null>(null);
  const [hideFab, setHideFab] = useState(false);
  const [showAddToShoppingList, setShowAddToShoppingList] = useState(false);
  const [selectedItemForShoppingList, setSelectedItemForShoppingList] = useState<InventoryItem | null>(null);
  const [selectedShoppingListId, setSelectedShoppingListId] = useState<string | null>(null);
  const [shoppingListQuantity, setShoppingListQuantity] = useState(1);
  const [shoppingListStore, setShoppingListStore] = useState('');
  const [shoppingListAisle, setShoppingListAisle] = useState('');
  const [shoppingListLink, setShoppingListLink] = useState('');
  const [shoppingListPrice, setShoppingListPrice] = useState('0');
  const [newShoppingListName, setNewShoppingListName] = useState('');
  const [showNewListForm, setShowNewListForm] = useState(false);
  const [showStoreSuggestions, setShowStoreSuggestions] = useState(false);
  const [storeSuggestionSearch, setStoreSuggestionSearch] = useState('');
  const [showAisleSuggestions, setShowAisleSuggestions] = useState(false);
  const [aisleSuggestionSearch, setAisleSuggestionSearch] = useState('');
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [viewMode, setViewMode] = useState<'gallery' | 'list' | 'shelf'>(() => {
    try {
      const stored = localStorage.getItem('inventoryViewMode');
      return (stored === 'gallery' || stored === 'list' || stored === 'shelf') ? stored : 'shelf';
    } catch {
      return 'shelf';
    }
  });
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);
  const [viewingItem, setViewingItem] = useState<InventoryItem | null>(null);
  const [tooltipMessage, setTooltipMessage] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{x: number; y: number} | null>(null);
  const [viewportWidth, setViewportWidth] = useState(() => 
    typeof window !== 'undefined' ? window.innerWidth : 400
  );

  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const itemsHeaderRef = useRef<HTMLHeadingElement | null>(null);
  
  const shoppingLists = useShoppingListStore(state => state.lists);
  const createShoppingList = useShoppingListStore(state => state.createList);
  const createShoppingListItem = useShoppingListStore(state => state.createItem);

  // Function to show tooltip for 1 second
  const showTooltip = (message: string, event: React.MouseEvent<HTMLButtonElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    
    // Ensure tooltip doesn't overflow on small screens
    // Shift left by 20px to give more room on the right
    const adjustedX = Math.min(centerX - 20, window.innerWidth - 100);
    
    setTooltipMessage(message);
    setTooltipPosition({
      x: adjustedX,
      y: rect.bottom + 8
    });
    setTimeout(() => {
      setTooltipMessage(null);
      setTooltipPosition(null);
    }, 1000);
  };
  const fetchShoppingLists = useShoppingListStore(state => state.fetchFromServer);
  
  // Save view mode preference to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('inventoryViewMode', viewMode);
    } catch {
      // ignore
    }
  }, [viewMode]);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    category: 'mat' as InventoryCategory,
    quantity: 1,
    unit: 'st',
    expirationDate: '',
    goal: 0,
    location: 'Hemma',
    notes: '',
    link: '',
    kcalPerUnit: undefined as number | undefined,
  });
  const [linkError, setLinkError] = useState('');

  // URL validation helper
  const validateUrl = (url: string): boolean => {
    if (!url || url.trim() === '') return true; // Empty is valid
    try {
      const urlObj = new URL(url);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  };

  // Hämta data från server vid mount (vänta på BÅDA hydrations)
  useEffect(() => {
    if (authHydrated && inventoryHydrated && isAuthenticated) {
      const familyId = useFamilyStore.getState().family?.id;
      fetchFromServer(familyId);
    }
  }, [authHydrated, inventoryHydrated, isAuthenticated, fetchFromServer]);
  
  // Handle search query from URL params (e.g. from recipe page)
  useEffect(() => {
    const searchParam = searchParams.get('search');
    if (searchParam) {
      setSearchQuery(searchParam);
      // Scroll to and focus search field after render
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          setTimeout(() => {
            searchInputRef.current?.focus();
          }, 300);
        }
      }, 300);
      // Remove the search param from URL
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  
  // Lyssna på resize för att uppdatera shelf-layout
  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Blockera scrolling när modal är öppen
  useEffect(() => {
    const isModalOpen = showAddForm || showSuggestions || showLocationSuggestions || confirmInventoryItem || confirmDeleteItemId || showAddToShoppingList || showStoreSuggestions || showAisleSuggestions || viewingItem;
    
    if (isModalOpen) {
      const scrollY = window.scrollY;
      document.body.dataset.scrollY = String(scrollY);
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    } else {
      const scrollY = document.body.dataset.scrollY;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY));
      }
    }
    
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    };
  }, [showAddForm, showSuggestions, showLocationSuggestions, confirmInventoryItem, confirmDeleteItemId, showAddToShoppingList, showStoreSuggestions, showAisleSuggestions, viewingItem]);

  // Dölj flytande Lägg till-knapp när mobilmenyn är öppen (Layout dispatchar 'mobile-menu')
  useEffect(() => {
    // initial state from sessionStorage
    try {
      const stored = sessionStorage.getItem('mobileMenuOpen');
      if (stored === 'true') setHideFab(true);
    } catch {
      // ignore
    }

    const handler = (e: Event) => {
      const detail = (e as CustomEvent<boolean>)?.detail;
      setHideFab(Boolean(detail));
    };

    window.addEventListener('mobile-menu', handler as EventListener);
    return () => window.removeEventListener('mobile-menu', handler as EventListener);
  }, []);

  // Unika platser från befintliga föremål
  const existingLocations = useMemo(() => {
    const locations = new Set(items.map(item => item.location));
    // Lägg alltid till Hemma som standardval
    locations.add('Hemma');
    return Array.from(locations).sort();
  }, [items]);

  // Filtrera platsförslag
  const filteredLocations = useMemo(() => {
    if (!locationSearch) return existingLocations;
    return existingLocations.filter(loc => 
      loc.toLowerCase().includes(locationSearch.toLowerCase())
    );
  }, [existingLocations, locationSearch]);

  // Hämta unika butiker från shopping list items
  const existingStores = useMemo(() => {
    const stores = new Set<string>();
    const shoppingListItems = useShoppingListStore.getState().items;
    shoppingListItems.forEach(item => {
      if (item.store) stores.add(item.store);
    });
    return Array.from(stores).sort();
  }, []);

  // Filtrera butiksförslag
  const filteredStores = useMemo(() => {
    if (!storeSuggestionSearch) return existingStores;
    return existingStores.filter(s => 
      s.toLowerCase().includes(storeSuggestionSearch.toLowerCase())
    );
  }, [existingStores, storeSuggestionSearch]);

  // Filtrera avdelningsförslag
  const filteredAisles = useMemo(() => {
    if (!aisleSuggestionSearch) return COMMON_AISLES;
    return COMMON_AISLES.filter(a => 
      a.toLowerCase().includes(aisleSuggestionSearch.toLowerCase())
    );
  }, [aisleSuggestionSearch]);

  // Filtrera förslag baserat på kategori och sökning
  const filteredSuggestions = useMemo(() => {
    const category = formData.category;
    const suggestions = COMMON_ITEMS[category] || [];
    if (!suggestionSearch) return suggestions;
    return suggestions.filter(s => 
      s.name.toLowerCase().includes(suggestionSearch.toLowerCase())
    );
  }, [formData.category, suggestionSearch]);

  // Filtrera items
  const filteredItems = items.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.location.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Beräkna totala kalorier från mat
  const totalKcal = items
    .filter(item => item.category === 'mat' && item.kcalPerUnit)
    .reduce((sum, item) => sum + (item.kcalPerUnit! * item.quantity), 0);

  // Beräkna antal dagar maten räcker baserat på faktiska kaloriinställningar
  const { familyMembers, userDailyCalories } = useSettingsStore();
  
  // Beräkna totalt dagligt kaloribehov från faktiska inställningar
  const totalDailyKcal = useMemo(() => {
    // Kontohavaren
    let total = userDailyCalories || 2000;
    
    // Familjemedlemmar (exkl husdjur)
    familyMembers.forEach(member => {
      if (member.role !== 'pet') {
        const defaultCalories = member.role === 'adult' ? 2000 : 1500;
        total += member.dailyCalories || defaultCalories;
      }
    });
    
    return total;
  }, [userDailyCalories, familyMembers]);

  const daysOfFood = totalDailyKcal > 0 ? Math.floor(totalKcal / totalDailyKcal) : 0;

  // Sortera efter valt fält
  const sortedItems = [...filteredItems].sort((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      case 'expiration':
        // Items utan utgångsdatum hamnar sist
        if (!a.expirationDate && !b.expirationDate) comparison = 0;
        else if (!a.expirationDate) comparison = 1;
        else if (!b.expirationDate) comparison = -1;
        else comparison = new Date(a.expirationDate).getTime() - new Date(b.expirationDate).getTime();
        break;
      case 'quantity':
        comparison = a.quantity - b.quantity;
        break;
      case 'inventoryDate':
        // Items utan inventeringsdatum hamnar sist
        if (!a.lastInventoryDate && !b.lastInventoryDate) comparison = 0;
        else if (!a.lastInventoryDate) comparison = 1;
        else if (!b.lastInventoryDate) comparison = -1;
        else comparison = new Date(a.lastInventoryDate).getTime() - new Date(b.lastInventoryDate).getTime();
        break;
    }
    
    return sortAsc ? comparison : -comparison;
  });

  // Kolla utgångsdatum
  const now = new Date();
  const oneMonthFromNow = new Date();
  oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);

  const getExpirationStatus = (item: InventoryItem) => {
    if (!item.expirationDate) return 'none';
    const expDate = new Date(item.expirationDate);
    if (expDate < now) return 'expired';
    if (expDate <= oneMonthFromNow) return 'expiring';
    return 'ok';
  };

  const handleExpirationBannerClick = () => {
    setViewMode('gallery');
    setSortBy('expiration');
    setSortAsc(true);
    setTimeout(() => {
      itemsHeaderRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleUninventoriedBannerClick = () => {
    setViewMode('gallery');
    setSortBy('inventoryDate');
    setSortAsc(true);
    setTimeout(() => {
      itemsHeaderRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleSubmit = () => {
    if (!formData.name || formData.quantity < 0) return;
    
    // Validate link
    if (formData.link && !validateUrl(formData.link)) {
      setLinkError('Ogiltig URL. Ange en giltig webbadress (t.ex. https://example.com)');
      return;
    }
    
    const itemData = {
      name: formData.name,
      category: formData.category,
      quantity: formData.quantity,
      unit: formData.unit,
      expirationDate: formData.expirationDate === '' ? null : formData.expirationDate,
      location: formData.location,
      notes: formData.notes === '' ? null : formData.notes,
      link: formData.link || undefined,
      kcalPerUnit: formData.category === 'mat' ? formData.kcalPerUnit : undefined,
      lastInventoryDate: editingItem?.lastInventoryDate || new Date().toISOString(),
      goal: typeof formData.goal === 'number' ? formData.goal : undefined,
    };
    
    const familyId = useFamilyStore.getState().family?.id;
    if (editingItem) {
      updateItem(editingItem.id, itemData, familyId);
    } else {
      addItem(itemData, familyId);
    }
    
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: selectedCategory !== 'all' ? selectedCategory : 'mat',
      quantity: 1,
      unit: 'st',
      expirationDate: '',
      goal: 0,
      location: 'Hemma',
      notes: '',
      link: '',
      kcalPerUnit: undefined,
    });
    setLinkError('');
    setShowAddForm(false);
    setEditingItem(null);
    setIsCopying(false);
  };

  // Öppna add-formuläret med rätt kategori
  const openAddForm = () => {
    // Check limits: Free tier = 30 items, Premium = 2000 items
    const hasPremiumAccess = user?.subscriptionTier === 'premium' || !!family;
    const FREE_TIER_LIMIT = 30;
    const PREMIUM_LIMIT = 2000;
    
    if (!hasPremiumAccess && items.length >= FREE_TIER_LIMIT) {
      setShowPremiumModal(true);
      return;
    }
    
    if (hasPremiumAccess && items.length >= PREMIUM_LIMIT) {
      // Show error toast for Premium limit
      // Premium users can't add more than 2000 items
      return;
    }

    setFormData(prev => ({
      ...prev,
      name: '',
      category: selectedCategory !== 'all' ? selectedCategory : 'mat',
      quantity: 1,
      unit: 'st',
      expirationDate: '',
      goal: 0,
      location: 'Hemma',
      notes: '',
      link: '',
      kcalPerUnit: undefined,
    }));
    setEditingItem(null);
    setIsCopying(false);
    setSuggestionSearch('');
    setShowSuggestions(false);
    setLocationSearch('');
    setShowLocationSuggestions(false);
    setShowAddForm(true);
  };

  // Kopiera ett befintligt föremål
  const copyItem = (item: InventoryItem) => {
    setFormData({
      name: item.name,
      category: item.category,
      quantity: item.quantity,
      unit: item.unit,
      expirationDate: '', // Tom utgångsdatum för kopian
      goal: item.goal || 0,
      location: item.location,
      notes: item.notes || '',
      link: item.link || '',
      kcalPerUnit: item.kcalPerUnit,
    });
    setEditingItem(null);
    setIsCopying(true);
    setSuggestionSearch('');
    setShowSuggestions(false);
    setLocationSearch('');
    setShowLocationSuggestions(false);
    setShowAddForm(true);
  };

  // Välj ett förslag från listan
  const selectSuggestion = (suggestion: { name: string; unit: string; kcalPerUnit?: number }) => {
    setFormData(prev => ({
      ...prev,
      name: suggestion.name,
      unit: suggestion.unit,
      kcalPerUnit: suggestion.kcalPerUnit,
    }));
    setShowSuggestions(false);
    setSuggestionSearch('');
  };

  const startEditing = (item: InventoryItem) => {
    setFormData({
      name: item.name,
      category: item.category,
      quantity: item.quantity,
      unit: item.unit,
      expirationDate: item.expirationDate || '',
      goal: typeof item.goal === 'number' ? item.goal : 0,
      location: item.location,
      notes: item.notes || '',
      link: item.link || '',
      kcalPerUnit: item.kcalPerUnit,
    });
    setEditingItem(item);
    setIsCopying(false);
    setShowAddForm(true);
  };

  const handleDelete = (id: string) => {
    setConfirmDeleteItemId(id);
  };

  // Snabbändra antal direkt i listan
  const handleQuickQuantityChange = (item: InventoryItem, delta: number) => {
    const newQuantity = Math.max(0, item.quantity + delta);
    updateItem(item.id, { quantity: newQuantity });
    
    // Uppdatera viewing modal om den är öppen för denna vara
    if (viewingItem && viewingItem.id === item.id) {
      setViewingItem({ ...viewingItem, quantity: newQuantity });
    }
  };

  return (
    <>
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold flex items-center"><Package className="h-6 w-6 text-primary mr-3 shrink-0" />{t('inventory.title')}</h1>
        <p className="text-muted-foreground mt-1">Håll koll på mat, medicin, utrustning och andra viktiga beredskapslager.</p>
      </div>

      {/* Varningar */}
      {(stats.expiredCount > 0 || stats.expiringCount > 0 || stats.uninventoriedCount > 0) && (
        <div className="space-y-3">
          {stats.expiredCount > 0 && (
            <div
              className="flex items-center justify-between w-full p-4 rounded-xl bg-red-500/10 border border-red-500/30 cursor-pointer"
              onClick={handleExpirationBannerClick}
            >
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-6 w-6 text-red-500" />
                <div>
                  <h3 className="font-semibold text-red-400">Utgånget</h3>
                  <p className="text-sm text-muted-foreground">
                    {stats.expiredCount} föremål har gått ut
                  </p>
                </div>
              </div>
            </div>
          )}
          {stats.expiringCount > 0 && (
            <div
              className="flex items-center justify-between w-full p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/30 cursor-pointer"
              onClick={handleExpirationBannerClick}
            >
              <div className="flex items-center gap-3">
                <Calendar className="h-6 w-6 text-yellow-500" />
                <div>
                  <h3 className="font-semibold text-yellow-400">Går ut snart</h3>
                  <p className="text-sm text-muted-foreground">
                    {stats.expiringCount} föremål inom 30 dagar
                  </p>
                </div>
              </div>
            </div>
          )}
          {stats.uninventoriedCount > 0 && (
            <div
              className="flex items-center justify-between w-full p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/30 cursor-pointer"
              onClick={handleUninventoriedBannerClick}
            >
              <div className="flex items-center gap-3">
                <CalendarCheck className="h-6 w-6 text-yellow-500" />
                <div>
                  <h3 className="font-semibold text-yellow-400">Ej inventerade</h3>
                  <p className="text-sm text-muted-foreground">
                    {stats.uninventoriedCount} föremål inte inventerade på {INVENTORY_REVIEW_INTERVAL_DAYS} dagar
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Vattenknapp */}
      <Link
        to="/app/water"
        className="flex items-center justify-between w-full p-4 rounded-xl bg-blue-400/10 border border-blue-400/30 hover:bg-blue-400/20 transition-all"
      >
        <div className="flex items-center gap-3">
          <Droplets className="h-6 w-6 text-blue-400" />
          <p className="font-semibold text-blue-400">Vattenförråd</p>
        </div>
        <div className="flex items-center gap-2">
          {waterBadgeCount > 0 && (
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full text-white text-[10px] font-medium" style={{ backgroundColor: '#945123' }}>{waterBadgeCount}</span>
          )}
          <ChevronRight className="h-5 w-5 text-blue-400" />
        </div>
      </Link>

      {/* Filterknappar */}
      <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
        <button
          onClick={() => {
            setSelectedCategory('all');
            setTimeout(() => {
              itemsHeaderRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
          }}
          className={`rounded-lg border p-2 text-center transition-colors aspect-square flex flex-col items-center justify-center ${
            selectedCategory === 'all'
              ? 'border-orange-500/50 bg-orange-500/20 text-white shadow-sm'
              : 'border-border bg-card text-muted-foreground hover:bg-muted'
          }`}
        >
          <Package className="h-5 w-5 sm:h-6 sm:w-6 lg:h-8 lg:w-8 mx-auto text-white/90" />
          <p className="text-xs sm:text-xs lg:text-sm text-white/80 mt-0.5 leading-tight">Alla</p>
          <p className="font-semibold text-sm sm:text-base lg:text-lg">{stats.totalItems}</p>
        </button>
        {VISIBLE_CATEGORIES.map(cat => {
          const config = CATEGORY_CONFIG[cat];
          if (!config) return null;
          const Icon = config.icon as unknown as ComponentType<SVGProps<SVGSVGElement>>;
          const count = stats.byCategory[cat] || 0;
          const isMatCategory = cat === 'mat';
            return (
            <button
              key={cat}
              onClick={() => {
                setSelectedCategory(selectedCategory === cat ? 'all' : cat);
                setTimeout(() => {
                  itemsHeaderRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 100);
              }}
              className={`rounded-lg border p-2 text-center transition-colors aspect-square flex flex-col items-center justify-center ${
                  selectedCategory === cat 
                    ? 'border-orange-500/50 bg-orange-500/20 text-white shadow-sm' 
                    : 'border-border bg-card text-muted-foreground hover:bg-muted'
                }`}
            >
              <Icon className="h-5 w-5 sm:h-6 sm:w-6 lg:h-8 lg:w-8 mx-auto" style={{ color: config.color }} />
              <p className="text-xs sm:text-xs lg:text-sm text-white/80 mt-0.5 leading-tight truncate w-full">{CATEGORY_LABELS[cat]}</p>
              <p className="font-semibold text-sm sm:text-base lg:text-lg">{count}</p>
              {isMatCategory && totalKcal > 0 && (
                <>
                  <p className="text-[10px] sm:text-xs lg:text-xs text-orange-400 leading-none">{totalKcal.toLocaleString('sv-SE')} kcal</p>
                  <p className="text-[10px] sm:text-xs lg:text-xs text-orange-400 leading-none">Ca {daysOfFood} dagar</p>
                </>
              )}
            </button>
          );
        })}
      </div>

      {/* Länk till Inköpslista (grön) placerad under filterboxarna */}
      <div className="mt-2 space-y-2">
        <Link
          to="/app/shopping-list"
          className="w-full rounded-xl p-4 transition-colors flex items-center justify-between group border"
          style={{ backgroundColor: 'rgba(167,139,250,0.15)', borderColor: 'rgba(167,139,250,0.15)' }}
        >
          <div className="flex items-center gap-3">
            <ShoppingCart className="h-5 w-5 text-purple-300" />
            <p className="text-sm font-semibold text-purple-300">{t('nav.shoppingList')}</p>
          </div>
          <ChevronRight className="h-5 w-5 text-purple-300 group-hover:translate-x-1 transition-transform" />
        </Link>
        
        <Link
          to="/app/recipes"
          className="w-full rounded-xl p-4 transition-colors flex items-center justify-between group border"
          style={{ backgroundColor: 'rgba(109, 124, 72, 0.15)', borderColor: 'rgba(109, 124, 72, 0.15)' }}
        >
          <div className="flex items-center gap-3">
            <ChefHat className="h-5 w-5" style={{ color: '#83a359' }} />
            <p className="text-sm font-semibold" style={{ color: '#83a359' }}>{t('nav.recipes')}</p>
          </div>
          <ChevronRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" style={{ color: '#83a359' }} />
        </Link>
      </div>

      {/* Sökfält och sortering */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  ref={searchInputRef}
            type="text"
            placeholder="Sök föremål / plats..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-10 py-2 rounded-lg border border-border bg-background"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              title="Rensa sökning"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="flex gap-1">
          <button
            onClick={(e) => {
              if (sortBy === 'name') setSortAsc(!sortAsc);
              else { setSortBy('name'); setSortAsc(true); }
              showTooltip('Sortera på namn', e);
            }}
            className={`p-2 rounded-lg border transition-colors ${
              sortBy === 'name' 
                ? 'border-primary bg-primary/10 text-primary' 
                : 'border-border hover:bg-muted text-muted-foreground'
            }`}
            title="Sortera på namn"
          >
            {sortBy === 'name' && !sortAsc ? <ArrowUpAZ className="h-4 w-4" /> : <ArrowDownAZ className="h-4 w-4" />}
          </button>
          <button
            onClick={(e) => {
              if (sortBy === 'expiration') setSortAsc(!sortAsc);
              else { setSortBy('expiration'); setSortAsc(true); }
              showTooltip('Sortera på utgångsdatum', e);
            }}
            className={`p-2 rounded-lg border transition-colors ${
              sortBy === 'expiration' 
                ? 'border-primary bg-primary/10 text-primary' 
                : 'border-border hover:bg-muted text-muted-foreground'
            }`}
            title="Sortera på utgångsdatum"
          >
            <CalendarDays className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              if (sortBy === 'quantity') setSortAsc(!sortAsc);
              else { setSortBy('quantity'); setSortAsc(true); }
              showTooltip('Sortera på antal', e);
            }}
            className={`p-2 rounded-lg border transition-colors ${
              sortBy === 'quantity' 
                ? 'border-primary bg-primary/10 text-primary' 
                : 'border-border hover:bg-muted text-muted-foreground'
            }`}
            title="Sortera på antal"
          >
            {sortBy === 'quantity' && !sortAsc ? <ArrowUp01 className="h-4 w-4" /> : <ArrowDown01 className="h-4 w-4" />}
          </button>
          <button
            onClick={(e) => {
              if (sortBy === 'inventoryDate') setSortAsc(!sortAsc);
              else { setSortBy('inventoryDate'); setSortAsc(true); }
              showTooltip('Sortera på inventeringsdatum', e);
            }}
            className={`p-2 rounded-lg border transition-colors ${
              sortBy === 'inventoryDate' 
                ? 'border-primary bg-primary/10 text-primary' 
                : 'border-border hover:bg-muted text-muted-foreground'
            }`}
            title="Sortera på inventeringsdatum"
          >
            <CalendarCheck className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Rubrik för lista */}
      <div className="flex items-center justify-between">
        <h2 ref={itemsHeaderRef} className="text-xl font-semibold">
          {selectedCategory === 'all' ? 'Alla föremål' : CATEGORY_LABELS[selectedCategory]}
        </h2>
        
        {/* View Mode Switcher */}
        <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
          <button
            onClick={(e) => {
              setViewMode('gallery');
              showTooltip('Gallerivy', e);
            }}
            className={`p-2 rounded transition-colors ${
              viewMode === 'gallery'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            title="Gallerivy"
          >
            <Grid2x2 className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              setViewMode('list');
              showTooltip('Listvy', e);
            }}
            className={`p-2 rounded transition-colors ${
              viewMode === 'list'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            title="Listvy"
          >
            <List className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              setViewMode('shelf');
              showTooltip('Skafferivy', e);
            }}
            className={`p-2 rounded transition-colors ${
              viewMode === 'shelf'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            title="Skafferivy"
          >
            <Layers className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Items lista */}
      {sortedItems.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-border p-12 text-center">
          <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">
            {searchQuery || selectedCategory !== 'all' 
              ? 'Inga föremål matchar din sökning'
              : t('inventory.empty')
            }
          </p>
          {!searchQuery && selectedCategory === 'all' && (
            <button
              onClick={openAddForm}
              className="mt-4 px-4 py-2 rounded-lg btn-primary-custom"
            >
              Lägg till ditt första föremål
            </button>
          )}
        </div>
      ) : viewMode === 'gallery' ? (
        // Gallery View - Full details in cards
        <div className="grid gap-3 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
          {sortedItems.map((item) => {
            const expStatus = getExpirationStatus(item);
            
            return (
              <div
                key={item.id}
                className={`rounded-lg border bg-card p-4 ${
                  expStatus === 'expired' 
                    ? 'border-red-500/50' 
                    : expStatus === 'expiring'
                      ? 'border-yellow-500/50'
                      : 'border-border'
                }`}
              >
                <div className="flex items-start justify-between gap-3 min-w-0">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {(() => {
                      const config = CATEGORY_CONFIG[item.category];
                      if (!config) {
                        return (
                          <div className="rounded-lg p-2 bg-muted flex-shrink-0">
                            <Package className="h-5 w-5 text-muted-foreground" />
                          </div>
                        );
                      }
                      const Icon = config.icon;
                      return (
                        <div 
                          className="rounded-lg p-2 flex-shrink-0"
                          style={{ backgroundColor: `${config.color}20` }}
                        >
                          <Icon className="h-5 w-5" style={{ color: config.color }} />
                        </div>
                      );
                    })()}
                    <div className="min-w-0 flex-1">
                      <button
                        type="button"
                        onClick={() => {
                          setSearchQuery(item.name);
                          // Do NOT focus the input here — focusing would open
                          // the on-screen keyboard on mobile devices.
                        }}
                        className="font-medium text-left w-full text-sm sm:text-base hover:underline truncate"
                        title="Sök efter detta namn"
                      >
                        {item.name}
                      </button>
                      <p className="text-sm text-muted-foreground truncate">
                        {CATEGORY_LABELS[item.category]}
                      </p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => handleQuickQuantityChange(item, -1)}
                        className="p-1 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                        title="Minska antal"
                      >
                        <Minus className="h-3 w-3" />
                      </button>
                      <span className="text-xl font-bold min-w-[2ch] text-center">{item.quantity}</span>
                      <button
                        onClick={() => handleQuickQuantityChange(item, 1)}
                        className="p-1 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                        title="Öka antal"
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                    </div>
                    <span className="text-sm text-muted-foreground">{item.unit}</span>
                    {item.category === 'mat' && item.kcalPerUnit && (
                      <p className="text-xs text-orange-400 font-medium">
                        {item.kcalPerUnit * item.quantity} kcal
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="mt-3 space-y-1 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <MapPin className="h-3 w-3 flex-shrink-0" />
                    <span className="soft-wrap">Plats: {item.location}</span>
                  </div>
                  {typeof item.goal === 'number' && item.goal > 0 && (() => {
                    const isBelowGoal = (typeof item.goal === 'number') && item.quantity < (item.goal || 0);
                    const textClass = isBelowGoal ? 'text-yellow-600' : '';
                    const iconClass = isBelowGoal ? 'text-yellow-500' : '';
                    return (
                      <div className="flex items-center gap-1">
                        <Target className={`h-3 w-3 ${iconClass}`} />
                        <span className={textClass}>
                          Beredskapsmål: <span className="font-semibold">{item.goal}</span> {item.unit}
                        </span>
                      </div>
                    );
                  })()}
                  {item.expirationDate && (
                    <div className={`flex items-center gap-1 ${
                      expStatus === 'expired' ? 'text-red-400' : 
                      expStatus === 'expiring' ? 'text-yellow-400' : ''
                    }`}>
                      <Calendar className="h-3 w-3" />
                      Bäst före: {new Date(item.expirationDate).toLocaleDateString('sv-SE')}
                      {expStatus === 'expired' && ' (utgått)'}
                      {expStatus === 'expiring' && ' (snart)'}
                    </div>
                  )}
                  {item.lastInventoryDate && (() => {
                    const inventoryDate = new Date(item.lastInventoryDate);
                    const today = new Date();
                    const inventoryThreshold = new Date();
                    inventoryThreshold.setDate(inventoryThreshold.getDate() - INVENTORY_REVIEW_INTERVAL_DAYS);
                    
                    const isToday = inventoryDate.toDateString() === today.toDateString();
                    const isOld = inventoryDate < inventoryThreshold;
                    
                    return (
                      <div className={`flex items-center gap-1 ${
                        isToday ? 'text-green-400' : 
                        isOld ? 'text-red-400' : ''
                      }`}>
                        <CheckCircle2 className="h-3 w-3" />
                        Inventerad: {inventoryDate.toLocaleDateString('sv-SE')}
                        {isOld && ' (för länge sedan!)'}
                      </div>
                    );
                  })()}
                </div>

                {item.notes && (
                  <p className="mt-2 text-xs text-muted-foreground italic whitespace-pre-wrap soft-wrap">
                    {item.notes}
                  </p>
                )}
                {item.link && (
                  <a
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline flex items-center gap-1 mt-2"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => { e.stopPropagation(); }}
                  >
                    <ExternalLink className="h-3 w-3" />
                    Länk
                  </a>
                )}
                
                <div className="mt-3 flex justify-start gap-2">
                  <button
                    onClick={() => setConfirmInventoryItem(item)}
                    className="p-1.5 rounded hover:bg-green-500/20 hover:text-green-400 transition-colors"
                    title="Markera som inventerad"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      setSelectedItemForShoppingList(item);
                      setShoppingListLink(item.link || '');
                      fetchShoppingLists().then(() => {
                        const primaryList = shoppingLists.find(l => l.isPrimary);
                        const defaultList = primaryList || shoppingLists[0];
                        setSelectedShoppingListId(defaultList?.id || '');
                        setShoppingListQuantity(1);
                      });
                      setShowAddToShoppingList(true);
                    }}
                    className="p-1.5 rounded hover:bg-blue-500/20 hover:text-blue-400 transition-colors"
                    title="Lägg till i inköpslista"
                  >
                    <ShoppingCart className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => copyItem(item)}
                    className="p-1.5 rounded hover:bg-muted transition-colors"
                    title="Kopiera"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => startEditing(item)}
                    className="p-1.5 rounded hover:bg-muted transition-colors"
                    title="Redigera"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="p-1.5 rounded hover:bg-red-500/20 hover:text-red-400 transition-colors"
                    title="Ta bort"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : viewMode === 'list' ? (
        // List View - Compact with expandable details
        <div className="space-y-2">
          {sortedItems.map((item) => {
            const expStatus = getExpirationStatus(item);
            const isExpanded = expandedItemId === item.id;
            
            return (
              <div
                key={item.id}
                className={`rounded-lg border bg-card overflow-hidden ${
                  expStatus === 'expired' 
                    ? 'border-red-500/50' 
                    : expStatus === 'expiring'
                      ? 'border-yellow-500/50'
                      : 'border-border'
                }`}
              >
                {/* Compact header - always visible */}
                <button
                  onClick={() => setExpandedItemId(isExpanded ? null : item.id)}
                  className="w-full p-3 flex items-start gap-3 hover:bg-muted/50 transition-colors text-left"
                >
                  {(() => {
                    const config = CATEGORY_CONFIG[item.category];
                    if (!config) {
                      return (
                        <div className="rounded-lg p-2 bg-muted flex-shrink-0">
                          <Package className="h-5 w-5 text-muted-foreground" />
                        </div>
                      );
                    }
                    const Icon = config.icon;
                    return (
                      <div 
                        className="rounded-lg p-2 flex-shrink-0"
                        style={{ backgroundColor: `${config.color}20` }}
                      >
                        <Icon className="h-5 w-5" style={{ color: config.color }} />
                      </div>
                    );
                  })()}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium soft-wrap">{item.name}</h3>
                    <p className="text-sm text-muted-foreground break-words">{CATEGORY_LABELS[item.category]}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-lg font-bold whitespace-nowrap">{item.quantity}</span>
                    <span className="text-sm text-muted-foreground whitespace-nowrap">{item.unit}</span>
                    <ChevronRight className={`h-5 w-5 text-muted-foreground transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                  </div>
                </button>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="px-4 pb-4 space-y-3 border-t border-border">
                    {/* Quantity controls */}
                    <div className="flex items-center justify-between pt-3">
                      <span className="text-sm font-medium">Antal</span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleQuickQuantityChange(item, -1);
                          }}
                          className="p-2 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                          title="Minska antal"
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                        <span className="text-2xl font-bold min-w-[3ch] text-center">{item.quantity}</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleQuickQuantityChange(item, 1);
                          }}
                          className="p-2 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                          title="Öka antal"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {/* Details */}
                    <div className="space-y-2 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 flex-shrink-0" />
                        <span className="soft-wrap">Plats: {item.location}</span>
                      </div>
                      {typeof item.goal === 'number' && item.goal > 0 && (() => {
                        const isBelowGoal = (typeof item.goal === 'number') && item.quantity < (item.goal || 0);
                        const textClass = isBelowGoal ? 'text-yellow-600' : '';
                        const iconClass = isBelowGoal ? 'text-yellow-500' : '';
                        return (
                          <div className="flex items-center gap-2">
                            <Target className={`h-4 w-4 ${iconClass}`} />
                            <span className={textClass}>
                              Beredskapsmål: <span className="font-semibold">{item.goal}</span> {item.unit}
                            </span>
                          </div>
                        );
                      })()}
                      {item.expirationDate && (
                        <div className={`flex items-center gap-2 ${
                          expStatus === 'expired' ? 'text-red-400' : 
                          expStatus === 'expiring' ? 'text-yellow-400' : ''
                        }`}>
                          <Calendar className="h-4 w-4" />
                          Bäst före: {new Date(item.expirationDate).toLocaleDateString('sv-SE')}
                          {expStatus === 'expired' && ' (utgått)'}
                          {expStatus === 'expiring' && ' (snart)'}
                        </div>
                      )}
                      {item.lastInventoryDate && (() => {
                        const inventoryDate = new Date(item.lastInventoryDate);
                        const today = new Date();
                        const inventoryThreshold = new Date();
                        inventoryThreshold.setDate(inventoryThreshold.getDate() - INVENTORY_REVIEW_INTERVAL_DAYS);
                        
                        const isToday = inventoryDate.toDateString() === today.toDateString();
                        const isOld = inventoryDate < inventoryThreshold;
                        
                        return (
                          <div className={`flex items-center gap-2 ${
                            isToday ? 'text-green-400' : 
                            isOld ? 'text-red-400' : ''
                          }`}>
                            <CheckCircle2 className="h-4 w-4" />
                            Inventerad: {inventoryDate.toLocaleDateString('sv-SE')}
                            {isOld && ' (för länge sedan!)'}
                          </div>
                        );
                      })()}
                      {item.category === 'mat' && item.kcalPerUnit && (
                        <div className="flex items-center gap-2 text-orange-400">
                          <Utensils className="h-4 w-4" />
                          {item.kcalPerUnit * item.quantity} kcal totalt
                        </div>
                      )}
                    </div>

                    {item.notes && (
                      <p className="text-sm text-muted-foreground italic whitespace-pre-wrap bg-muted/30 p-2 rounded soft-wrap">
                        {item.notes}
                      </p>
                    )}
                    {item.link && (
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline flex items-center gap-2"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => { e.stopPropagation(); }}
                      >
                        <ExternalLink className="h-4 w-4" />
                        Öppna länk
                      </a>
                    )}

                    {/* Action buttons */}
                    <div className="flex justify-start gap-2 pt-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmInventoryItem(item);
                        }}
                        className="p-2 rounded hover:bg-green-500/20 hover:text-green-400 transition-colors"
                        title="Markera som inventerad"
                      >
                        <CheckCircle2 className="h-5 w-5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedItemForShoppingList(item);
                          setShoppingListLink(item.link || '');
                          fetchShoppingLists().then(() => {
                            const primaryList = shoppingLists.find(l => l.isPrimary);
                            const defaultList = primaryList || shoppingLists[0];
                            setSelectedShoppingListId(defaultList?.id || '');
                            setShoppingListQuantity(1);
                          });
                          setShowAddToShoppingList(true);
                        }}
                        className="p-2 rounded hover:bg-blue-500/20 hover:text-blue-400 transition-colors"
                        title="Lägg till i inköpslista"
                      >
                        <ShoppingCart className="h-5 w-5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          copyItem(item);
                        }}
                        className="p-2 rounded hover:bg-muted transition-colors"
                        title="Kopiera"
                      >
                        <Copy className="h-5 w-5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          startEditing(item);
                        }}
                        className="p-2 rounded hover:bg-muted transition-colors"
                        title="Redigera"
                      >
                        <Pencil className="h-5 w-5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(item.id);
                        }}
                        className="p-2 rounded hover:bg-red-500/20 hover:text-red-400 transition-colors"
                        title="Ta bort"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        // Shelf View - Virtual bookshelf with colored boxes in columns
        <div className="space-y-0 max-w-full overflow-x-hidden">
          {/* Calculate shelves - organize items in columns (2 items per column) */}
          {(() => {
            const shelves: InventoryItem[][] = [];
            const gap = 6; // Gap between columns - reduced for more density
            
            // Calculate actual available width considering padding
            // Padding values match the px-4 sm:px-4 lg:px-10 xl:px-12 classes
            const containerPadding = viewportWidth < 640 ? 32 : viewportWidth < 1024 ? 32 : viewportWidth < 1280 ? 80 : 96;
            const availableWidth = viewportWidth - containerPadding;
            
            // Dynamic column calculation based on available space
            // Set min/max item sizes and ideal number of columns
            const minItemSize = 70;  // Minimum box size to fit content
            const maxItemSize = viewportWidth < 1536 ? 130 : 110; // Larger max for smaller screens, smaller for large screens
            const idealColumns = viewportWidth < 640 ? 4 : viewportWidth < 1024 ? 6 : viewportWidth < 1536 ? 8 : viewportWidth < 1920 ? 10 : 12;
            
            // Calculate what item size would give us the ideal number of columns
            let calculatedItemSize = (availableWidth - (gap * (idealColumns - 1))) / idealColumns;
            
            // Clamp between min and max to keep boxes reasonable
            calculatedItemSize = Math.max(minItemSize, Math.min(maxItemSize, calculatedItemSize));
            
            // Calculate actual columns that fit with the clamped size
            const columnWidth = calculatedItemSize + gap;
            const columnsPerShelf = Math.max(4, Math.floor((availableWidth + gap) / columnWidth));
            const itemsPerShelf = columnsPerShelf * 2; // 2 items per column
            
            // Group items into shelves based on available width
            for (let i = 0; i < sortedItems.length; i += itemsPerShelf) {
              shelves.push(sortedItems.slice(i, i + itemsPerShelf));
            }
            
            // Ensure minimum 3 shelves (add empty shelves if needed)
            while (shelves.length < 3) {
              shelves.push([]);
            }
            
            return shelves.map((shelfItems, shelfIndex) => {
              // Organize items into columns (2 items per column)
              const columns: InventoryItem[][] = [];
              for (let i = 0; i < shelfItems.length; i += 2) {
                columns.push(shelfItems.slice(i, Math.min(i + 2, shelfItems.length)));
              }
              
              const isFirstShelf = shelfIndex === 0;
              const isLastShelf = shelfIndex === shelves.length - 1;
              let borderRadiusClass = '';
              if (isFirstShelf && isLastShelf) {
                borderRadiusClass = 'rounded-xl';
              } else if (isFirstShelf) {
                borderRadiusClass = 'rounded-t-xl';
              } else if (isLastShelf) {
                borderRadiusClass = 'rounded-b-xl';
              }
              
              return (
              <div 
                key={shelfIndex}
                className={`relative min-h-[220px] overflow-hidden max-w-full ${borderRadiusClass}`}
                style={{
                  backgroundImage: 'url(/hylla.png)',
                  backgroundSize: '100% 100%',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                }}
              >
                {/* Light overlay for better box visibility */}
                <div className="absolute inset-0 bg-black/40"></div>
                
                {/* Items as colored boxes in columns */}
                <div className="absolute bottom-[25px] left-0 right-0 z-10 flex justify-start items-end px-4 sm:px-4 lg:px-10 xl:px-12" style={{ gap: '6px' }}>
                  {columns.map((columnItems, colIndex) => (
                    <div key={colIndex} className="flex flex-col-reverse items-end" style={{ gap: '2px' }}>
                      {columnItems.map((item) => {
                        const config = CATEGORY_CONFIG[item.category];
                        const color = config?.color || '#9e9e9e';
                        const IconComponent = config?.icon;
                  
                        // Size based on quantity - reduced range to fit shelf height better
                        const minSize = 40;
                        const maxSize = 110;
                        const sizeRange = maxSize - minSize;
                        const logQuantity = Math.log10(item.quantity + 1);
                        const maxLogQuantity = Math.log10(100);
                        const normalizedSize = Math.min(logQuantity / maxLogQuantity, 1);
                        const size = minSize + (sizeRange * normalizedSize);
                        
                        const darkenColor = (hexColor: string) => {
                          const hex = hexColor.replace('#', '');
                          const r = Math.round(parseInt(hex.substr(0, 2), 16) * 0.25);
                          const g = Math.round(parseInt(hex.substr(2, 2), 16) * 0.25);
                          const b = Math.round(parseInt(hex.substr(4, 2), 16) * 0.25);
                          return `rgb(${r}, ${g}, ${b})`;
                        };
                        
                        const darkColor = darkenColor(color);
                        
                        return (
                          <button
                            key={item.id}
                            onClick={() => setViewingItem(item)}
                            className="group relative transition-all hover:scale-110 hover:z-20 rounded-lg overflow-hidden flex flex-col items-center justify-center p-2"
                            style={{
                              width: `${size}px`,
                              height: `${size}px`,
                              backgroundColor: darkColor,
                              border: `1px solid ${color}`,
                              boxShadow: `
                                0 0 8px ${color}, 
                                0 0 16px ${color}60, 
                                0 6px 12px rgba(0,0,0,0.7), 
                                0 3px 6px rgba(0,0,0,0.5),
                                inset 2px 2px 4px rgba(255,255,255,0.1),
                                inset -2px -2px 4px rgba(0,0,0,0.3)
                              `,
                              background: `linear-gradient(135deg, ${darkColor} 0%, ${color}40 100%)`,
                              transform: 'perspective(500px) rotateX(2deg)',
                            }}
                            title={`${item.name} (${item.quantity} ${item.unit})`}
                          >
                            {IconComponent && (
                              <div className="absolute top-1 left-1 z-10" style={{ color }}>
                                <IconComponent 
                                  size={size > 120 ? 20 : size > 80 ? 16 : 12}
                                  strokeWidth={2.5}
                                />
                              </div>
                            )}
                            <div className="absolute top-1 right-1 bg-black/70 text-white font-bold rounded px-1 py-0.5 z-10" style={{ fontSize: '10px' }}>
                              {item.goal ? `${item.quantity}/${item.goal}` : item.quantity}
                            </div>
                            <div className="text-white text-center break-words leading-tight" style={{
                              fontSize: size > 120 ? '13px' : size > 80 ? '11px' : '9px',
                              textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                              fontWeight: '600',
                              maxWidth: '100%',
                              overflow: 'hidden',
                              display: '-webkit-box',
                              WebkitLineClamp: size > 120 ? 4 : size > 80 ? 3 : 2,
                              WebkitBoxOrient: 'vertical',
                            }}>
                              <div>{item.name}</div>
                              {item.location && (
                                <div style={{
                                  fontSize: size > 120 ? '10px' : size > 80 ? '9px' : '7px',
                                  opacity: 0.8,
                                  marginTop: '2px'
                                }}>
                                  {item.location}
                                </div>
                              )}
                            </div>
                            <div className="absolute inset-0 bg-black/90 text-white p-2 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                              <span className="text-sm font-bold text-center break-words line-clamp-3">{item.name}</span>
                              <span className="text-xs mt-1">{item.quantity} {item.unit}</span>
                              {item.location && <span className="text-[10px] mt-1 text-gray-300">{item.location}</span>}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
              );
            });
          })()}
        </div>
      )}

      {/* Item Details Modal (for shelf view and other views) */}
      {viewingItem && (
        <ModalBackdrop>
          <div className="w-full max-w-md rounded-xl bg-card border border-border p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold soft-wrap pr-2">{viewingItem.name}</h2>
              <button
                onClick={() => setViewingItem(null)}
                className="p-1 rounded hover:bg-muted transition-colors flex-shrink-0"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Category badge */}
            <div className="mb-4">
              {(() => {
                const config = CATEGORY_CONFIG[viewingItem.category];
                if (!config) return null;
                const Icon = config.icon;
                return (
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ backgroundColor: `${config.color}20` }}>
                    <Icon className="h-4 w-4" style={{ color: config.color }} />
                    <span className="text-sm font-medium">{CATEGORY_LABELS[viewingItem.category]}</span>
                  </div>
                );
              })()}
            </div>

            {/* Quantity */}
            <div className="mb-4 flex items-center justify-between">
              <span className="text-sm font-medium">Antal</span>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleQuickQuantityChange(viewingItem, -1)}
                  className="p-2 rounded hover:bg-muted transition-colors"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span className="text-2xl font-bold min-w-[3ch] text-center">{viewingItem.quantity} {viewingItem.unit}</span>
                <button
                  onClick={() => handleQuickQuantityChange(viewingItem, 1)}
                  className="p-2 rounded hover:bg-muted transition-colors"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>

            {viewingItem.category === 'mat' && viewingItem.kcalPerUnit && (
              <div className="mb-4 p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
                <div className="flex items-center gap-2">
                  <Utensils className="h-4 w-4 text-orange-400" />
                  <span className="text-sm font-medium text-orange-400">
                    {viewingItem.kcalPerUnit * viewingItem.quantity} kcal totalt
                  </span>
                </div>
              </div>
            )}

            {/* Details list */}
            <div className="space-y-3 mb-4">
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <span className="soft-wrap">Plats: {viewingItem.location}</span>
              </div>

              {typeof viewingItem.goal === 'number' && viewingItem.goal > 0 && (() => {
                const isBelowGoal = viewingItem.quantity < viewingItem.goal;
                return (
                  <div className={`flex items-center gap-2 text-sm ${isBelowGoal ? 'text-yellow-600' : ''}`}>
                    <Target className={`h-4 w-4 ${isBelowGoal ? 'text-yellow-500' : 'text-muted-foreground'}`} />
                    <span>Beredskapsmål: <span className="font-semibold">{viewingItem.goal}</span> {viewingItem.unit}</span>
                  </div>
                );
              })()}

              {viewingItem.expirationDate && (() => {
                const expStatus = getExpirationStatus(viewingItem);
                return (
                  <div className={`flex items-center gap-2 text-sm ${
                    expStatus === 'expired' ? 'text-red-400' : 
                    expStatus === 'expiring' ? 'text-yellow-400' : ''
                  }`}>
                    <Calendar className="h-4 w-4" />
                    <span>
                      Bäst före: {new Date(viewingItem.expirationDate).toLocaleDateString('sv-SE')}
                      {expStatus === 'expired' && ' (utgått)'}
                      {expStatus === 'expiring' && ' (snart)'}
                    </span>
                  </div>
                );
              })()}

              {viewingItem.lastInventoryDate && (() => {
                const inventoryDate = new Date(viewingItem.lastInventoryDate);
                const today = new Date();
                const inventoryThreshold = new Date();
                inventoryThreshold.setDate(inventoryThreshold.getDate() - INVENTORY_REVIEW_INTERVAL_DAYS);
                
                const isToday = inventoryDate.toDateString() === today.toDateString();
                const isOld = inventoryDate < inventoryThreshold;
                
                return (
                  <div className={`flex items-center gap-2 text-sm ${
                    isToday ? 'text-green-400' : 
                    isOld ? 'text-red-400' : ''
                  }`}>
                    <CheckCircle2 className="h-4 w-4" />
                    <span>
                      Inventerad: {inventoryDate.toLocaleDateString('sv-SE')}
                      {isOld && ' (för länge sedan!)'}
                    </span>
                  </div>
                );
              })()}
            </div>

            {viewingItem.notes && (
              <div className="mb-4 p-3 rounded-lg bg-muted/50">
                <p className="text-sm whitespace-pre-wrap soft-wrap">{viewingItem.notes}</p>
              </div>
            )}

            {viewingItem.link && (
              <a
                href={viewingItem.link}
                target="_blank"
                rel="noopener noreferrer"
                className="mb-4 flex items-center gap-2 text-sm text-primary hover:underline"
              >
                <ExternalLink className="h-4 w-4" />
                Öppna länk
              </a>
            )}

            {/* Action buttons */}
            <div className="flex flex-wrap gap-2 pt-4 border-t border-border">
              <button
                onClick={() => {
                  setConfirmInventoryItem(viewingItem);
                  setViewingItem(null);
                }}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30 transition-colors"
              >
                <CheckCircle2 className="h-4 w-4" />
                Inventera
              </button>
              <button
                onClick={() => {
                  setSelectedItemForShoppingList(viewingItem);
                  setShoppingListLink(viewingItem.link || '');
                  fetchShoppingLists().then(() => {
                    const primaryList = shoppingLists.find(l => l.isPrimary);
                    const defaultList = primaryList || shoppingLists[0];
                    setSelectedShoppingListId(defaultList?.id || '');
                    setShoppingListQuantity(1);
                  });
                  setShowAddToShoppingList(true);
                  setViewingItem(null);
                }}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-purple-300 transition-colors"
                style={{ backgroundColor: 'rgba(167,139,250,0.15)' }}
              >
                <ShoppingCart className="h-4 w-4" />
                Inköpslista
              </button>
            </div>

            <div className="flex flex-wrap gap-2 mt-2">
              <button
                onClick={() => {
                  copyItem(viewingItem);
                  setViewingItem(null);
                }}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
              >
                <Copy className="h-4 w-4" />
                Kopiera
              </button>
              <button
                onClick={() => {
                  startEditing(viewingItem);
                  setViewingItem(null);
                }}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
              >
                <Pencil className="h-4 w-4" />
                Redigera
              </button>
              <button
                onClick={() => {
                  handleDelete(viewingItem.id);
                  setViewingItem(null);
                }}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
              >
                <Trash2 className="h-4 w-4" />
                Ta bort
              </button>
            </div>
          </div>
        </ModalBackdrop>
      )}

      {/* Add/Edit Modal */}
      {showAddForm && (
        <div className="fixed z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, margin: 0 }}>
          <div className="w-full max-w-md rounded-xl bg-card border border-border p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">
                {editingItem ? 'Redigera föremål' : isCopying ? 'Kopiera föremål' : 'Lägg till föremål'}
              </h2>
              <button
                onClick={resetForm}
                className="p-1 rounded hover:bg-muted transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Kategori överst */}
              <div>
                <label className="block text-sm font-medium mb-1">Kategori</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value as InventoryCategory })}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                >
                  {VISIBLE_CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>
                      {CATEGORY_LABELS[cat]}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Namn *</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="T.ex. Havregryn"
                    maxLength={50}
                    className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-border bg-background break-words"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSuggestions(true)}
                    className="p-2 rounded-lg border border-border hover:bg-muted transition-colors flex-shrink-0"
                    title="Sök förslag"
                  >
                    <Search className="h-5 w-5" />
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Plats</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="Hemma"
                    maxLength={50}
                    className="flex-1 px-3 py-2 rounded-lg border border-border bg-background break-words"
                  />
                  <button
                    type="button"
                    onClick={() => setShowLocationSuggestions(true)}
                    className="p-2 rounded-lg border border-border hover:bg-muted transition-colors"
                    title="Välj plats"
                  >
                    <Search className="h-5 w-5" />
                  </button>
                </div>
              </div>
              
              <div className="max-w-xs">
                <label className="block text-sm font-medium mb-1">Antal</label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, quantity: Math.max(0, formData.quantity - 1) })}
                    className="p-2 rounded-lg border border-border hover:bg-muted transition-colors flex-shrink-0"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <input
                    type="number"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: Math.min(1000, Math.max(0, Number(e.target.value))) })}
                    min={0}
                    max={1000}
                    className="w-20 px-3 py-2 rounded-lg border border-border bg-background text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, quantity: Math.min(1000, formData.quantity + 1) })}
                    className="p-2 rounded-lg border border-border hover:bg-muted transition-colors flex-shrink-0"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              <div className="max-w-xs">
                <label className="block text-sm font-medium mb-1">Beredskapsmål</label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, goal: Math.max(0, (formData.goal || 0) - 1) })}
                    className="p-2 rounded-lg border border-border hover:bg-muted transition-colors flex-shrink-0"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <input
                    type="number"
                    value={formData.goal}
                    onChange={(e) => setFormData({ ...formData, goal: Math.min(1000, Math.max(0, Number(e.target.value))) })}
                    min={0}
                    max={1000}
                    className="w-20 px-3 py-2 rounded-lg border border-border bg-background text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, goal: Math.min(1000, (formData.goal || 0) + 1) })}
                    className="p-2 rounded-lg border border-border hover:bg-muted transition-colors flex-shrink-0"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Enhet</label>
                <select
                  value={formData.unit}
                  onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                >
                  <option value="st">st</option>
                  <option value="kg">kg</option>
                  <option value="g">g</option>
                  <option value="l">liter</option>
                  <option value="ml">ml</option>
                  <option value="förp">förp</option>
                  <option value="burk">burk</option>
                  <option value="påse">påse</option>
                </select>
              </div>

              {/* kcal per enhet - endast för mat */}
              {formData.category === 'mat' && (
                <div className="max-w-xs">
                  <label className="block text-sm font-medium mb-1">Kalorier per enhet (kcal)</label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, kcalPerUnit: Math.max(0, (formData.kcalPerUnit || 0) - 100) })}
                      className="p-2 rounded-lg border border-border hover:bg-muted transition-colors flex-shrink-0"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <input
                      type="number"
                      value={formData.kcalPerUnit || 0}
                      onChange={(e) => setFormData({ ...formData, kcalPerUnit: Math.min(10000, Math.max(0, Number(e.target.value))) })}
                      min={0}
                      max={10000}
                      className="w-20 px-3 py-2 rounded-lg border border-border bg-background text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, kcalPerUnit: Math.min(10000, (formData.kcalPerUnit || 0) + 100) })}
                      className="p-2 rounded-lg border border-border hover:bg-muted transition-colors flex-shrink-0"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                  {(formData.kcalPerUnit ?? 0) > 0 && formData.quantity > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Totalt: <span className="font-medium text-primary">{(formData.kcalPerUnit ?? 0) * formData.quantity} kcal</span>
                    </p>
                  )}
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium mb-1">
                  Utgångsdatum
                  {formData.expirationDate && (
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, expirationDate: '' })}
                      className="ml-2 text-xs text-muted-foreground hover:text-primary"
                    >
                      (rensa)
                    </button>
                  )}
                </label>
                <input
                  type="date"
                  value={formData.expirationDate}
                  onChange={(e) => setFormData({ ...formData, expirationDate: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Anteckningar</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Valfria anteckningar..."
                  maxLength={200}
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background resize-none break-words"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Länk</label>
                <input
                  type="url"
                  value={formData.link}
                  onChange={(e) => {
                    setFormData({ ...formData, link: e.target.value });
                    if (linkError) setLinkError('');
                  }}
                  onBlur={(e) => {
                    const url = e.target.value;
                    if (url && !validateUrl(url)) {
                      setLinkError('Ogiltig URL. Ange en giltig webbadress (t.ex. https://example.com)');
                    }
                  }}
                  placeholder="https://example.com"
                  className={`w-full px-3 py-2 rounded-lg border ${linkError ? 'border-red-500' : 'border-border'} bg-background`}
                />
                {linkError && (
                  <p className="text-sm text-red-500 mt-1">{linkError}</p>
                )}
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={resetForm}
                className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={handleSubmit}
                disabled={!formData.name || formData.quantity < 0}
                className="flex-1 px-4 py-2 rounded-lg btn-primary-custom transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <CheckCircle2 className="h-4 w-4" />
                {editingItem ? 'Spara' : 'Lägg till'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Förslag modal */}
      {showSuggestions && (
        <div className="fixed z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, margin: 0 }}>
          <div className="w-full max-w-md rounded-xl bg-card border border-border p-4 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                Vanliga {CATEGORY_LABELS[formData.category].toLowerCase()}
              </h3>
              <button
                onClick={() => {
                  setShowSuggestions(false);
                  setSuggestionSearch('');
                }}
                className="p-1 rounded hover:bg-muted transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Sökfält */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                value={suggestionSearch}
                onChange={(e) => setSuggestionSearch(e.target.value)}
                placeholder="Sök..."
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-background"
              />
            </div>
            
            {/* Lista med förslag */}
            <div className="flex-1 overflow-y-auto space-y-2">
              {filteredSuggestions.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  Inga förslag hittades
                </p>
              ) : (
                filteredSuggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => selectSuggestion(suggestion)}
                    className="w-full text-left px-3 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
                  >
                    <span className="font-medium">{suggestion.name}</span>
                    <span className="text-sm text-muted-foreground ml-2">
                      ({suggestion.unit})
                      {suggestion.kcalPerUnit && ` • ${suggestion.kcalPerUnit} kcal`}
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Platsförslag modal */}
      {showLocationSuggestions && (
        <div className="fixed z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, margin: 0 }}>
          <div className="w-full max-w-md rounded-xl bg-card border border-border p-4 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                Välj plats
              </h3>
              <button
                onClick={() => {
                  setShowLocationSuggestions(false);
                  setLocationSearch('');
                }}
                className="p-1 rounded hover:bg-muted transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Sökfält */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                value={locationSearch}
                onChange={(e) => setLocationSearch(e.target.value)}
                placeholder="Sök eller skriv ny plats..."
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-background"
              />
            </div>
            
            {/* Lista med platser */}
            <div className="flex-1 overflow-y-auto space-y-2">
              {/* Visa söktermen som ett alternativ om den inte finns */}
              {locationSearch && !existingLocations.some(loc => loc.toLowerCase() === locationSearch.toLowerCase()) && (
                <button
                  onClick={() => {
                    setFormData(prev => ({ ...prev, location: locationSearch }));
                    setShowLocationSuggestions(false);
                    setLocationSearch('');
                  }}
                  className="w-full text-left px-3 py-2 rounded-lg border border-primary bg-primary/10 hover:bg-primary/20 transition-colors"
                >
                  <span className="font-medium">+ Lägg till "{locationSearch}"</span>
                </button>
              )}
              {filteredLocations.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  Inga platser hittades
                </p>
              ) : (
                filteredLocations.map((location, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setFormData(prev => ({ ...prev, location }));
                      setShowLocationSuggestions(false);
                      setLocationSearch('');
                    }}
                    className="w-full text-left px-3 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
                  >
                    <span className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{location}</span>
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bekräftelsedialog för inventering */}
      {confirmInventoryItem && (
        <div className="fixed z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, margin: 0 }}>
          <div className="w-full max-w-sm rounded-xl bg-card border border-border p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-green-500/20">
                <CheckCircle2 className="h-6 w-6 text-green-400" />
              </div>
              <h2 className="text-xl font-semibold">Bekräfta inventering</h2>
            </div>
            <p className="text-muted-foreground mb-2">
              Vill du markera <span className="font-medium text-foreground">{confirmInventoryItem.name}</span> som inventerad?
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              Datumet för senast inventerad uppdateras till idag.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmInventoryItem(null)}
                className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={() => {
                  updateItem(confirmInventoryItem.id, { lastInventoryDate: new Date().toISOString() });
                  setConfirmInventoryItem(null);
                }}
                className="flex-1 px-4 py-2 rounded-lg bg-green-500 text-white hover:bg-green-600 transition-colors"
              >
                Bekräfta
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bekräftelsedialog för borttagning */}
      {confirmDeleteItemId && (() => {
        const item = items.find(i => i.id === confirmDeleteItemId);
        return (
          <div className="fixed z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, margin: 0 }}>
            <div className="w-full max-w-sm rounded-xl bg-card border border-border p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-red-500/20">
                  <Trash2 className="h-6 w-6 text-red-400" />
                </div>
                <h2 className="text-xl font-semibold">Bekräfta borttagning</h2>
              </div>
              <p className="text-muted-foreground mb-2">
                Är du säker på att du vill ta bort <span className="font-medium text-foreground">{item?.name}</span>?
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                Detta föremål kommer att tas bort permanent.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmDeleteItemId(null)}
                  className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
                >
                  Avbryt
                </button>
                <button
                    onClick={() => {
                    const familyId = useFamilyStore.getState().family?.id;
                    deleteItem(confirmDeleteItemId, familyId);
                    setConfirmDeleteItemId(null);
                  }}
                  className="flex-1 px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors"
                >
                  Ta bort
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal för att lägga till i inköpslista */}
      {showAddToShoppingList && selectedItemForShoppingList && (
        <div className="fixed z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, margin: 0 }}>
          <div className="w-full max-w-md rounded-xl bg-card border border-border p-6 my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/20">
                  <ShoppingCart className="h-6 w-6 text-primary" />
                </div>
                <h2 className="text-xl font-semibold">Lägg till i inköpslista</h2>
              </div>
              <button
                onClick={() => {
                    setShowAddToShoppingList(false);
                    setSelectedItemForShoppingList(null);
                    setSelectedShoppingListId(null);
                    setShoppingListQuantity(1);
                    setShowNewListForm(false);
                    setNewShoppingListName('');
                    setShoppingListStore('');
                    setShoppingListAisle('');
                    setShoppingListLink('');
                    setShoppingListPrice('0');
                }}
                className="p-1 rounded hover:bg-muted flex-shrink-0"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-muted-foreground mb-1">Artikel</p>
              <p className="font-medium">{selectedItemForShoppingList.name}</p>
            </div>

            <div className="mb-4">
              <p className="text-sm text-muted-foreground mb-1">Kategori</p>
              <p className="font-medium">{INVENTORY_TO_SHOPPING_CATEGORY[selectedItemForShoppingList.category as InventoryCategory] || selectedItemForShoppingList.category}</p>
            </div>

            {shoppingListLink && (
              <div className="mb-4">
                <p className="text-sm text-muted-foreground mb-1">Länk</p>
                <a href={shoppingListLink} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline flex items-center gap-1">
                  <ExternalLink className="h-4 w-4" />
                  Länk
                </a>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Antal</label>
              <div className="flex items-center gap-2 max-w-xs">
                <button
                  onClick={() => setShoppingListQuantity(Math.max(1, shoppingListQuantity - 1))}
                  className="p-2 rounded-lg border border-border hover:bg-muted transition-colors flex-shrink-0"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <input
                  type="number"
                  min="1"
                  max="1000"
                  value={shoppingListQuantity}
                  onChange={(e) => setShoppingListQuantity(Math.min(1000, Math.max(1, parseInt(e.target.value) || 1)))}
                  className="w-20 px-3 py-2 rounded-lg border border-border bg-background text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <button
                  onClick={() => setShoppingListQuantity(Math.min(1000, shoppingListQuantity + 1))}
                  className="p-2 rounded-lg border border-border hover:bg-muted transition-colors flex-shrink-0"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Pris (valfritt)</label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShoppingListPrice(prev => { const cur = parseFloat((prev || '0').replace(',', '.')) || 0; const n = Math.max(0, cur - 100); return n % 1 === 0 ? String(n) : String(n).replace('.', ','); })}
                  disabled={parseFloat((shoppingListPrice || '0').replace(',', '.')) <= 0}
                  className="p-2 rounded-lg border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <div className="flex items-center gap-1">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={shoppingListPrice}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/[^0-9,.]/g, '').replace('.', ',').replace(/,{2,}/g, ',');
                      const parts = raw.split(',');
                      const cleaned = parts[0].slice(0, 7) + (parts.length > 1 ? ',' + parts[1].slice(0, 2) : '');
                      if (cleaned === '' || cleaned === ',') { setShoppingListPrice('0'); return; }
                      const numVal = parseFloat(cleaned.replace(',', '.'));
                      if (!isNaN(numVal) && numVal <= 1000000) setShoppingListPrice(cleaned);
                    }}
                    className="w-24 px-3 py-2 text-center rounded-lg border border-border bg-background"
                  />
                  <span className="text-sm text-muted-foreground">kr</span>
                </div>
                <button
                  type="button"
                  onClick={() => setShoppingListPrice(prev => { const cur = parseFloat((prev || '0').replace(',', '.')) || 0; const n = Math.min(1000000, cur + 100); return n % 1 === 0 ? String(n) : String(n).replace('.', ','); })}
                  className="p-2 rounded-lg border border-border hover:bg-muted transition-colors"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Butik (valfritt)</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={shoppingListStore}
                  onChange={(e) => setShoppingListStore(e.target.value)}
                  maxLength={50}
                  className="flex-1 px-3 py-2 rounded-lg border border-border bg-background"
                  placeholder="T.ex. ICA Maxi"
                />
                <button
                  type="button"
                  onClick={() => {
                    setStoreSuggestionSearch('');
                    setShowStoreSuggestions(true);
                  }}
                  className="p-2 rounded-lg border border-border hover:bg-muted transition-colors"
                  title="Visa befintliga butiker"
                >
                  <Search className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Hylla/Avdelning (valfritt)</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={shoppingListAisle}
                  onChange={(e) => setShoppingListAisle(e.target.value)}
                  maxLength={50}
                  className="flex-1 px-3 py-2 rounded-lg border border-border bg-background"
                  placeholder="T.ex. Mejeri"
                />
                <button
                  type="button"
                  onClick={() => {
                    setAisleSuggestionSearch('');
                    setShowAisleSuggestions(true);
                  }}
                  className="p-2 rounded-lg border border-border hover:bg-muted transition-colors"
                  title="Visa avdelningar"
                >
                  <Search className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">
                Välj inköpslista
              </label>
              
              {showNewListForm ? (
                <div className="space-y-3">
                  <input
                    type="text"
                    value={newShoppingListName}
                    onChange={(e) => setNewShoppingListName(e.target.value)}
                    placeholder="Namn på ny lista"
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary focus:border-transparent"
                    autoFocus
                  />
                  <button
                    onClick={() => {
                      setShowNewListForm(false);
                      setNewShoppingListName('');
                      // Återställ till primär/första listan
                      const primary = shoppingLists.find(l => l.isPrimary);
                      setSelectedShoppingListId(primary?.id || shoppingLists[0]?.id || null);
                    }}
                    className="text-sm text-primary hover:underline"
                  >
                    ← Tillbaka till befintliga listor
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Befintliga listor */}
                  {shoppingLists.length > 0 && (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {shoppingLists.map(list => (
                        <label
                          key={list.id}
                          className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted/50 cursor-pointer transition-colors"
                        >
                          <input
                            type="radio"
                            name="shopping-list"
                            checked={selectedShoppingListId === list.id}
                            onChange={() => setSelectedShoppingListId(list.id)}
                            className="text-primary focus:ring-primary"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{list.name}</span>
                              {list.isPrimary && (
                                <span className="text-yellow-500" title="Primär lista">
                                  ★
                                </span>
                              )}
                            </div>
                            {list.store && (
                              <span className="text-xs text-muted-foreground">{list.store}</span>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  )}

                  {/* Knapp för att skapa ny lista */}
                  <button
                    onClick={() => setShowNewListForm(true)}
                    className="w-full px-4 py-2 rounded-lg border border-dashed border-border transition-colors text-sm flex items-center justify-center gap-2 btn-primary-custom"
                  >
                    <Plus className="h-4 w-4" />
                    Skapa ny inköpslista
                  </button>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowAddToShoppingList(false);
                  setSelectedItemForShoppingList(null);
                  setSelectedShoppingListId(null);
                  setShoppingListQuantity(1);
                  setShowNewListForm(false);
                  setNewShoppingListName('');
                  setShoppingListStore('');
                  setShoppingListAisle('');
                  setShoppingListLink('');
                  setShoppingListPrice('0');
                }}
                className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={async () => {
                  if (showNewListForm) {
                    // Skapa ny lista och lägg till artikel
                    if (!newShoppingListName.trim()) return;
                    const newList = await createShoppingList({
                      name: newShoppingListName,
                      isPrimary: shoppingLists.length === 0, // Första listan blir primär
                    });
                    if (newList && selectedItemForShoppingList) {
                      await createShoppingListItem({
                        listId: newList.id,
                        name: selectedItemForShoppingList.name,
                        category: INVENTORY_TO_SHOPPING_CATEGORY[selectedItemForShoppingList.category],
                        quantity: shoppingListQuantity,
                        price: parseFloat((shoppingListPrice || '0').replace(',', '.')) || 0,
                        store: shoppingListStore || undefined,
                          aisle: shoppingListAisle || undefined,
                          link: selectedItemForShoppingList.link || undefined,
                      });
                    }
                  } else {
                    // Lägg till i befintlig lista
                    if (!selectedShoppingListId || !selectedItemForShoppingList) return;
                    // Hitta listans förvalda butik (om någon)
                    const targetList = shoppingLists.find(l => l.id === selectedShoppingListId);
                    const resolvedStore = shoppingListStore?.trim() ? shoppingListStore : (targetList?.store || undefined);
                    await createShoppingListItem({
                      listId: selectedShoppingListId,
                      name: selectedItemForShoppingList.name,
                      category: INVENTORY_TO_SHOPPING_CATEGORY[selectedItemForShoppingList.category],
                      quantity: shoppingListQuantity,
                      price: parseFloat((shoppingListPrice || '0').replace(',', '.')) || 0,
                      store: resolvedStore || undefined,
                      aisle: shoppingListAisle || undefined,
                      link: selectedItemForShoppingList.link || undefined,
                    });
                  }

                  // Stäng modal
                  setShowAddToShoppingList(false);
                  setSelectedItemForShoppingList(null);
                  setSelectedShoppingListId(null);
                  setShoppingListQuantity(1);
                  setShowNewListForm(false);
                  setNewShoppingListName('');
                  setShoppingListStore('');
                  setShoppingListAisle('');
                  setShoppingListPrice('0');
                }}
                disabled={showNewListForm ? !newShoppingListName.trim() : !selectedShoppingListId}
                className="flex-1 px-4 py-2 rounded-lg btn-primary-custom transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                  Lägg till
                </button>
            </div>
          </div>
        </div>
      )}
    </div>

    {/* Store Suggestions Modal */}
    {showStoreSuggestions && (
      <div className="fixed z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, margin: 0 }}>
        <div className="w-full max-w-md rounded-xl bg-card border border-border p-4 max-h-[80vh] flex flex-col my-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Välj butik</h3>
            <button onClick={() => setShowStoreSuggestions(false)} className="p-1 rounded hover:bg-muted">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="mb-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                value={storeSuggestionSearch}
                onChange={(e) => setStoreSuggestionSearch(e.target.value)}
                placeholder="Sök butik..."
                className="w-full pl-10 pr-3 py-2 rounded-lg border border-border bg-background"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto space-y-1">
            {filteredStores.map((store) => (
              <button
                key={store}
                onClick={() => {
                  setShoppingListStore(store);
                  setShowStoreSuggestions(false);
                }}
                className="w-full text-left px-3 py-2 rounded-lg hover:bg-muted transition-colors"
              >
                {store}
              </button>
            ))}
            {filteredStores.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                Inga butiker hittades
              </p>
            )}
          </div>
        </div>
      </div>
    )}

    {/* Aisle Suggestions Modal */}
    {showAisleSuggestions && (
      <div className="fixed z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, margin: 0 }}>
        <div className="w-full max-w-md rounded-xl bg-card border border-border p-4 max-h-[80vh] flex flex-col my-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Välj avdelning</h3>
            <button onClick={() => setShowAisleSuggestions(false)} className="p-1 rounded hover:bg-muted">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="mb-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                value={aisleSuggestionSearch}
                onChange={(e) => setAisleSuggestionSearch(e.target.value)}
                placeholder="Sök avdelning..."
                className="w-full pl-10 pr-3 py-2 rounded-lg border border-border bg-background"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto space-y-1">
            {filteredAisles.map((aisle) => (
              <button
                key={aisle}
                onClick={() => {
                  setShoppingListAisle(aisle);
                  setShowAisleSuggestions(false);
                }}
                className="w-full text-left px-3 py-2 rounded-lg hover:bg-muted transition-colors"
              >
                {aisle}
              </button>
            ))}
            {filteredAisles.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                Inga avdelningar hittades
              </p>
            )}
          </div>
        </div>
      </div>
    )}

    {/* Flytande Lägg till-knapp */}
    {!showAddForm && !showSuggestions && !showLocationSuggestions && !confirmInventoryItem && !confirmDeleteItemId && !showAddToShoppingList && !viewingItem && !hideFab && (
      <button
        onClick={openAddForm}
        className="fixed top-[3.75rem] right-4 z-50 rounded-full p-3 shadow-lg btn-primary-custom hover:scale-105 transition-transform"
        style={{ borderRadius: '9999px' }}
        title="Lägg till föremål"
      >
        <Plus className="h-5 w-5 text-white" />
      </button>
    )}

    {/* Tooltip notification */}
    {tooltipMessage && tooltipPosition && (
      <div 
        className="fixed z-50 bg-card border border-border rounded-lg shadow-lg px-3 py-2 animate-in fade-in slide-in-from-top-1 duration-200"
        style={{
          left: `${tooltipPosition.x}px`,
          top: `${tooltipPosition.y}px`,
          transform: 'translateX(-50%)'
        }}
      >
        <p className="text-sm font-medium text-foreground whitespace-nowrap">{tooltipMessage}</p>
      </div>
    )}

    {/* Premium Required Modal */}
    <PremiumRequiredModal
      open={showPremiumModal}
      onClose={() => setShowPremiumModal(false)}
      feature="Fler än 50 föremål i Förråd"
    />
    </>
  );
}
